import { Checkbox, FormControlLabel, Grid } from "@mui/material";
import { FormikProps } from "formik";
import CustomTextArea from "../../../../../../../../../components/CustomTextArea";
import Flex from "../../../../../../../../../components/Flex";
import Text from "../../../../../../../../../components/Text";
import CustomTitle from "../../../../../../../../../components/app/CustomTitle";
import PsychoeducationTherapyForm from "./PsychoeducationTherapyForm";
import { Form2Payload } from "../../../../../../scheduling/appointment/encounter/encounter-constant";
import CustomSelect from "../../../../../../../../../components/CustomSelect";
import CptCodesField from "../components/form-components/CptCodesField";
import IcdCodesField from "../components/form-components/IcdCodesField";

const SUICIDAL_IDEATION_OPTIONS = [
  { value: "None", label: "None" },
  { value: "Passive", label: "Passive" },
  { value: "Active without plan", label: "Active without plan" },
  { value: "Active with plan", label: "Active with plan" },
];

const HOMICIDAL_IDEATION_OPTIONS = [
  { value: "NonReported", label: "Non-Reported" },
  { value: "PresentAssessd", label: "Present-Assessd" },
];

const OVERALL_RISK_LEVEL_OPTIONS = [
  { value: "Low", label: "Low" },
  { value: "Moderate", label: "Moderate" },
  { value: "High", label: "High" },
  { value: "Imminent", label: "Imminent" },
];

const URGENCY_LEVEL_OPTIONS = [
  { value: "Routine", label: "Routine" },
  { value: "Urgent", label: "Urgent" },
  { value: "Emergent", label: "Emergent" },
];

/**
 * Fields that only apply while "Psychiatric consultation needed" is checked.
 * Unchecking hides them, so their values must be cleared too — otherwise the
 * stale text/urgency is still saved and printed on the signed note.
 */
const PSYCHIATRIC_CONSULTATION_FIELDS = [
  "reasonForConsultation",
  "urgencyLevel",
] as const;

/** Fields captured by the nested Psychoeducation Therapy form. */
const PSYCHOEDUCATION_SESSION_FIELDS = [
  "primaryTopic",
  "learningObjectives",
  "contentDelivered",
  "skillsCategoriesCovered",
  "skillsDescription",
  "materialsDistributed",
  "materialDetails",
  "engagementLevel",
  "comprehensionLevel",
  "questionsDiscussion",
  "participantFeedback",
  "inSessionPractice",
  "homePracticeAssignments",
  "realLifeApplicationPlan",
  "followUpTopics",
  "additionalResourcesNeeded",
  "nextSessionPlan",
  "psychoeducationProviderNote",
] as const;

interface CollaborativeCareModelFormProps {
  formik: FormikProps<Form2Payload>;
}

const CollaborativeCareModelForm = ({
  formik,
}: CollaborativeCareModelFormProps) => {
  const psychiatricConsultationNeeded =
    formik.values?.psychiatricConsultationNeeded ?? false;

  /**
   * Toggles a gating checkbox and, when it is turned off, clears every field
   * that section owns in the same update so nothing stale is submitted.
   */
  const handleSectionToggle = (
    field: keyof Form2Payload,
    checked: boolean,
    dependentFields: readonly string[]
  ) => {
    formik.setValues((prev) => ({
      ...prev,
      [field]: checked,
      ...(checked
        ? {}
        : Object.fromEntries(dependentFields.map((name) => [name, ""]))),
    }));
  };

  return (
    <Flex p={1.5}>
      <Grid container spacing={2}>
        {/* Behavioral Health Diagnosis */}
        <Flex flexDirection="column" gap={1} ml={2} width="100%">
          <CustomTitle>Behavioral Health Diagnosis</CustomTitle>
        </Flex>
        <Grid item xs={12}>
          <Flex flexDirection="row" gap={2} flexWrap="wrap">
            <IcdCodesField formik={formik} />
            <CptCodesField formik={formik} />
          </Flex>
        </Grid>
        <Grid item xs={12}>
          <CustomTextArea
            label="Secondary Diagnoses"
            rows={1}
            name="secondaryDiagnoses"
            placeholder="Additional diagnoses with ICD-10 codes..."
            onBlur={formik.handleBlur}
            value={formik.values?.secondaryDiagnoses ?? ""}
            onChange={formik.handleChange}
          />
        </Grid>

        {/* Treatment to Target */}
        <Flex flexDirection="column" gap={1} ml={2} width="100%" mt={2}>
          <CustomTitle>Treatment to Target</CustomTitle>
        </Flex>
        <Grid item xs={12} md={6}>
          <CustomTextArea
            label="Target Symptom"
            rows={1}
            name="targetSymptom"
            placeholder="e.g., Depressed mood, Anxiety"
            onBlur={formik.handleBlur}
            value={formik.values?.targetSymptom ?? ""}
            onChange={formik.handleChange}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <CustomTextArea
            label="Measurement Tool"
            rows={1}
            name="measurementTool"
            placeholder="e.g., PHQ-9, GAD-7"
            onBlur={formik.handleBlur}
            value={formik.values?.measurementTool ?? ""}
            onChange={formik.handleChange}
          />
        </Grid>
        <Grid item xs={12}>
          <CustomTextArea
            label="Treatment Goal"
            rows={1}
            name="treatmentGoal"
            placeholder="e.g., Reduce PHQ-9 score to < 5 (remission) within 12 weeks"
            onBlur={formik.handleBlur}
            value={formik.values?.treatmentGoal ?? ""}
            onChange={formik.handleChange}
          />
        </Grid>
        <Grid item xs={12}>
          <CustomTextArea
            label="Current Progress Toward Goal"
            rows={3}
            name="currentProgress"
            placeholder="Describe current progress, response to treatment, and any adjustments needed..."
            onBlur={formik.handleBlur}
            value={formik.values?.currentProgress ?? ""}
            onChange={formik.handleChange}
          />
        </Grid>

        {/* Medication Management */}
        <Flex flexDirection="column" gap={1} ml={2} width="100%" mt={2}>
          <CustomTitle>Medication Management</CustomTitle>
        </Flex>
        <Grid item xs={12}>
          <CustomTextArea
            label="Current Psychiatric Medications"
            rows={3}
            name="currentPsychiatricMedications"
            placeholder="List current medications with doses..."
            onBlur={formik.handleBlur}
            value={formik.values?.currentPsychiatricMedications ?? ""}
            onChange={formik.handleChange}
          />
        </Grid>
        <Grid item xs={12}>
          <CustomTextArea
            label="Medication Changes This Visit"
            rows={3}
            name="medicationChangesThisVisit"
            placeholder="New medications, dose changes, discontinuations..."
            onBlur={formik.handleBlur}
            value={formik.values?.medicationChangesThisVisit ?? ""}
            onChange={formik.handleChange}
          />
        </Grid>
        <Grid item xs={12}>
          <CustomTextArea
            label="Side Effects Reported"
            rows={3}
            name="sideEffectsReported"
            placeholder="Any side effects or adverse reactions..."
            onBlur={formik.handleBlur}
            value={formik.values?.sideEffectsReported ?? ""}
            onChange={formik.handleChange}
          />
        </Grid>
        <Grid item xs={12}>
          <CustomTextArea
            label="Adherence Issues or Barriers"
            rows={3}
            name="adherenceIssuesOrBarriers"
            placeholder="Document adherence status and any barriers to medication compliance..."
            onBlur={formik.handleBlur}
            value={formik.values?.adherenceIssuesOrBarriers ?? ""}
            onChange={formik.handleChange}
          />
        </Grid>

        {/* Brief Behavioral Interventions */}
        <Flex flexDirection="column" gap={1} ml={2} width="100%" mt={2}>
          <CustomTitle>Brief Behavioral Interventions</CustomTitle>
        </Flex>
        <Grid item xs={12} md={6}>
          <CustomTextArea
            label="Behavioral Activation"
            rows={3}
            name="behavioralActivation"
            placeholder="Activities scheduled, engagement level..."
            onBlur={formik.handleBlur}
            value={formik.values?.behavioralActivation ?? ""}
            onChange={formik.handleChange}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <CustomTextArea
            label="Psychoeducation Provided"
            rows={3}
            name="psychoeducationProvided"
            placeholder="Education topics covered..."
            onBlur={formik.handleBlur}
            value={formik.values?.psychoeducationProvided ?? ""}
            onChange={formik.handleChange}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <CustomTextArea
            label="Problem Solving"
            rows={3}
            name="problemSolving"
            placeholder="Problems addressed, solutions discussed..."
            onBlur={formik.handleBlur}
            value={formik.values?.problemSolving ?? ""}
            onChange={formik.handleChange}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <CustomTextArea
            label="Relapse Prevention"
            rows={3}
            name="relapsePrevention"
            placeholder="Warning signs identified, coping strategies..."
            onBlur={formik.handleBlur}
            value={formik.values?.relapsePrevention ?? ""}
            onChange={formik.handleChange}
          />
        </Grid>

        {/* Care Coordination Activities */}
        <Flex flexDirection="column" gap={1} ml={2} width="100%" mt={2}>
          <CustomTitle>Care Coordination Activities</CustomTitle>
        </Flex>
        <Grid item xs={12} md={6}>
          <CustomTextArea
            label="PCP Communication"
            rows={3}
            name="pcpCommunication"
            placeholder="Updates shared with primary care provider..."
            onBlur={formik.handleBlur}
            value={formik.values?.pcpCommunication ?? ""}
            onChange={formik.handleChange}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <CustomTextArea
            label="Specialty Referrals"
            rows={3}
            name="specialtyReferrals"
            placeholder="Referrals made or follow-up on existing referrals...."
            onBlur={formik.handleBlur}
            value={formik.values?.specialtyReferrals ?? ""}
            onChange={formik.handleChange}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <CustomTextArea
            label="Community Resources"
            rows={3}
            name="communityResources"
            placeholder="Resources provided or linkages made..."
            onBlur={formik.handleBlur}
            value={formik.values?.communityResources ?? ""}
            onChange={formik.handleChange}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <CustomTextArea
            label="Family/Caregiver Involvement"
            rows={3}
            name="familyCaregiverInvolvement"
            placeholder="Family contacts, education provided..."
            onBlur={formik.handleBlur}
            value={formik.values?.familyCaregiverInvolvement ?? ""}
            onChange={formik.handleChange}
          />
        </Grid>

        {/* Safety Assessment */}
        <Flex flexDirection="column" gap={1} ml={2} width="100%" mt={2}>
          <CustomTitle>Safety Assessment</CustomTitle>
        </Flex>
        <Grid item xs={12} md={6}>
          <Text mb={1}>Suicidal Ideation</Text>
          <CustomSelect
            name="suicidalIdeation"
            value={formik.values?.suicidalIdeation ?? ""}
            options={SUICIDAL_IDEATION_OPTIONS}
            disableSortOptions
            placeholder="Select"
            onChange={(v) => formik.setFieldValue("suicidalIdeation", v)}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Text mb={1}>Homicidal Ideation</Text>
          <CustomSelect
            name="homicidalIdeation"
            value={formik.values?.homicidalIdeation ?? ""}
            options={HOMICIDAL_IDEATION_OPTIONS}
            placeholder="Select"
            onChange={(v) => formik.setFieldValue("homicidalIdeation", v)}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Text mb={1}>Overall Risk Level</Text>
          <CustomSelect
            name="overallRiskLevel"
            value={formik.values?.overallRiskLevel ?? ""}
            options={OVERALL_RISK_LEVEL_OPTIONS}
            disableSortOptions
            placeholder="Select"
            onChange={(v) => formik.setFieldValue("overallRiskLevel", v)}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Text mb={1}>Safety Plan Status</Text>
          <CustomTextArea
            label="Safety Plan Status"
            rows={1}
            name="safetyPlanStatus"
            placeholder="e.g., Updated, In place, Created today"
            onBlur={formik.handleBlur}
            value={formik.values?.safetyPlanStatus ?? ""}
            onChange={formik.handleChange}
          />
        </Grid>

        {/* Treatment Plan & Next Steps */}
        <Flex flexDirection="column" gap={1} ml={2} width="100%" mt={2}>
          <CustomTitle>Treatment Plan & Next Steps</CustomTitle>
        </Flex>
        <Grid item xs={12}>
          <CustomTextArea
            label="Treatment Plan Changes"
            rows={3}
            name="treatmentPlanChanges"
            placeholder="Any modifications to treatment approach..."
            onBlur={formik.handleBlur}
            value={formik.values?.treatmentPlanChanges ?? ""}
            onChange={formik.handleChange}
          />
        </Grid>
        <Grid item xs={12}>
          <CustomTextArea
            label="Patient Response to Treatment"
            rows={3}
            name="patientResponseToTreatment"
            placeholder="Patient engagement, adherence, response..."
            onBlur={formik.handleBlur}
            value={formik.values?.patientResponseToTreatment ?? ""}
            onChange={formik.handleChange}
          />
        </Grid>
        <Grid item xs={12}>
          <CustomTextArea
            label="Barriers to Treatment"
            rows={3}
            name="barriersToTreatment"
            placeholder="Identified barriers and strategies to address..."
            onBlur={formik.handleBlur}
            value={formik.values?.barriersToTreatment ?? ""}
            onChange={formik.handleChange}
          />
        </Grid>
        <Grid item xs={12}>
          <CustomTextArea
            label="Next Steps & Action Items"
            rows={3}
            name="nextStepsAndActionItems"
            placeholder="Specific action items for patient and care team..."
            onBlur={formik.handleBlur}
            value={formik.values?.nextStepsAndActionItems ?? ""}
            onChange={formik.handleChange}
          />
        </Grid>

        {/* Psychiatric Consultation */}
        <Flex flexDirection="column" gap={1} ml={2} width="100%" mt={2}>
          <CustomTitle>Psychiatric Consultation</CustomTitle>
        </Flex>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={psychiatricConsultationNeeded}
                onChange={(e) =>
                  handleSectionToggle(
                    "psychiatricConsultationNeeded",
                    e.target.checked,
                    PSYCHIATRIC_CONSULTATION_FIELDS
                  )
                }
              />
            }
            label={<Text>Psychiatric consultation needed</Text>}
          />
        </Grid>
        {psychiatricConsultationNeeded && (
          <>
            <Grid item xs={12}>
              <CustomTextArea
                label="Reason for Consultation"
                rows={3}
                name="reasonForConsultation"
                placeholder="Clinical question, treatment not responding, complex case..."
                onBlur={formik.handleBlur}
                value={formik.values?.reasonForConsultation ?? ""}
                onChange={formik.handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <Text mb={1}>Urgency Level</Text>
              <CustomSelect
                name="urgencyLevel"
                value={formik.values?.urgencyLevel ?? ""}
                options={URGENCY_LEVEL_OPTIONS}
                disableSortOptions
                placeholder="Select urgency..."
                onChange={(v) => formik.setFieldValue("urgencyLevel", v)}
              />
            </Grid>
          </>
        )}
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                name="psychoeducationSessionRequired"
                checked={!!formik.values?.psychoeducationSessionRequired}
                onChange={(e) =>
                  handleSectionToggle(
                    "psychoeducationSessionRequired",
                    e.target.checked,
                    PSYCHOEDUCATION_SESSION_FIELDS
                  )
                }
              />
            }
            label={
              <Text variant="body1" fontWeight={500}>
                Psychoeducation Session Required?
              </Text>
            }
          />
        </Grid>
        {formik.values?.psychoeducationSessionRequired && (
          <Grid item xs={12}>
            <PsychoeducationTherapyForm formik={formik} />
          </Grid>
        )}
      </Grid>
    </Flex>
  );
};

export default CollaborativeCareModelForm;
